// Stores
import { useAppDataStore } from '@/stores/appDataStore';
import { useUserDataStore } from '@/stores/userDataStore';

// Types
import { MemberDetails } from '@/types/user';
import { MealPackagesResponse, RegionsResponse } from '@/types/apiResponses';

// Utils
import {
  fetchAllEventsDetails,
  fetchAllMealsDetails,
  fetchAllRegisteredEntrants,
  fetchCompletionProgress,
  fetchEntrantTypes,
  fetchEventClasses,
  fetchEventContacts,
  fetchEventDetails,
  fetchGenders,
  fetchMemberDetails,
  fetchPCAAgeRanges,
  fetchPaymentSummary,
  fetchResources,
  fetchPrimaryEntrantDetails,
  fetchRegions,
  fetchShirtSizes,
  fetchTimeSlots,
  fetchVehicleBodyStyle,
  fetchVehicleColors,
  fetchMealPackages,
} from '@/api/apiCalls';

export const fetchInitData = async (): Promise<void> => {
  const userDataStore = useUserDataStore();
  const appDataStore = useAppDataStore();

  // Sequential API calls
  const allEventsDetailsResponse = await fetchAllEventsDetails();
  const allMealsDetailsResponse = await fetchAllMealsDetails();
  const allRegisteredEntrantsResponse = await fetchAllRegisteredEntrants();
  const completionProgressResponse = await fetchCompletionProgress();
  const entrantTypesResponse = await fetchEntrantTypes();
  const eventClassesResponse = await fetchEventClasses();
  const eventContactsResponse = await fetchEventContacts();
  const eventDetailsResponse = await fetchEventDetails();
  const gendersResponse = await fetchGenders();
  const mealPackagesResponse = await fetchMealPackages();
  const memberDetailsResponse = await fetchMemberDetails();
  const pcaAgeRangesResponse = await fetchPCAAgeRanges();
  const primaryEntrantDetailsResponse = await fetchPrimaryEntrantDetails();
  const regionsResponse = await fetchRegions();
  const resourcesResponse = await fetchResources();
  const shirtSizesResponse = await fetchShirtSizes();
  const timeSlotsResponse = await fetchTimeSlots();
  const vehicleBodyStylesResponse = await fetchVehicleBodyStyle();
  const vehicleColorsResponse = await fetchVehicleColors();

  // Using the REGIONCODE we're provided for the MemberDetails, look up the corresponding REGIONID
  // (we'll need it for form submissions later)
  const regionAPIResponseItem: RegionsResponse[number] | undefined = regionsResponse.find((region: RegionsResponse[number]) => region.REGIONCODE === memberDetailsResponse.REGIONCODE);

  if (regionAPIResponseItem === undefined) {
    throw new Error(`initData.ts: could not find a RegionsResponse item with REGIONCODE ${memberDetailsResponse.REGIONCODE}`);
  }

  appDataStore.setInitData({
    allEventsDetails: allEventsDetailsResponse,
    allMealsDetails: allMealsDetailsResponse,
    entrantTypes: entrantTypesResponse,
    eventClasses: eventClassesResponse,
    eventContacts: eventContactsResponse,
    eventDetails: eventDetailsResponse,
    genders: gendersResponse,
    mealPackages: mealPackagesResponse,
    pcaAgeRanges: pcaAgeRangesResponse,
    regions: regionsResponse,
    resources: resourcesResponse,
    shirtSizes: shirtSizesResponse,
    timeSlots: timeSlotsResponse,
    vehicleBodyStyles: vehicleBodyStylesResponse,
    vehicleColors: vehicleColorsResponse,
  });

  userDataStore.setMemberDetailsData({
    ...memberDetailsResponse,
    REGIONID: regionAPIResponseItem.REGIONID,
  } as MemberDetails);

  userDataStore.setPrimaryEntrantDetailsData(primaryEntrantDetailsResponse);
  userDataStore.setAllRegisteredEntrants(allRegisteredEntrantsResponse);

  if (completionProgressResponse !== null) {
    // The completion progress endpoint returns null if there is 0% completion
    // If that's the case, we don't need to do anything, just leave the defaults as-is
    userDataStore.setCompletionProgress(completionProgressResponse);
  }

  await userDataStore.requestPaymentSummary();

  appDataStore.setIsLoading(false);
  appDataStore.setInitialLoadComplete(true);
};
